import {
    FileText,
    Binary,
    BrainCircuit,
    Layers3,
    Activity,
    Move,
    LayoutGrid,
    History,
    CandlestickChart,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
    Container,
    Section,
    PageHero,
    SectionHeading,
    CardImage,
} from "@/components/Primitives";

const FEATURES = [
    {
        icon: FileText,
        title: "Transparent decision log.",
        body: "Every enter / wait / pass / hold / exit decision is saved with the structural reasoning behind it. Re-read any trade like a journal that filled itself in.",
        img: "/images/cards/decision_log.png",
    },
    {
        icon: Binary,
        title: "Deterministic signal engine.",
        body: "A full stack of technical analysis computed in code — same inputs, same outputs, every time.",
        img: "/images/cards/signal_engine.png",
    },
    {
        icon: BrainCircuit,
        title: "Reasoning-model trader.",
        body: "A modern LLM makes the actual call from a clean brief — pattern recognition over structured facts, not a hard-coded rule tree.",
        img: "/images/cards/reasoning_model.png",
    },
    {
        icon: Layers3,
        title: "Conviction-based sizing.",
        body: "Position size and risk scale with how strong the setup reads, on a defined tier system — not a flat bet on everything.",
        img: "/images/cards/conviction_sizing.png",
    },
    {
        icon: Activity,
        title: "Volatility-aware stops.",
        body: "Stop distance is derived from each instrument's own range, with a minimum floor, so risk is proportional to the market.",
        img: "/images/cards/volatility_stops.png",
    },
    {
        icon: Move,
        title: "Live position management.",
        body: "Targets, break-even shifts, and trailing logic handled automatically, with the model gating each scale-out.",
        img: "/images/cards/position_management.png",
    },
    {
        icon: LayoutGrid,
        title: "Multi-ticker dashboard.",
        body: "Watch every active name on one screen — live candles, plan levels overlaid (entry / stop / targets), and the current state of each agent.",
        img: "/images/cards/multi_ticker.png",
    },
    {
        icon: History,
        title: "Backtest mode.",
        body: "Replay any past session through the full pipeline with a single anchor timestamp.",
        img: "/images/cards/backtest.png",
    },
    {
        icon: CandlestickChart,
        title: "Stocks and futures.",
        body: "Two instrument engines, one uncompromising standard.",
        img: "/images/cards/stocks_futures.png",
    },
];

export default function Platform() {
    return (
        <>
            <Seo
                title="Platform"
                description="Everything you get with The Aegis Line: a transparent decision log, a deterministic signal engine, a reasoning-model trader, conviction-based sizing, volatility-aware stops and a multi-ticker dashboard."
            />
            <PageHero
                eyebrow="Features / what you get"
                title="A trading desk you can read back."
                sub="Every figure, price and decision renders in mono — a signal that the data is computed, not written."
            />

            <Section>
                <Container>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {FEATURES.map((f, i) => (
                            <Reveal key={f.title} delay={(i % 3) * 0.08}>
                                <div className="group h-full overflow-hidden rounded-lg border border-haze bg-ink-soft transition-colors hover:border-rule/40">
                                    <CardImage
                                        src={f.img}
                                        alt={f.title}
                                        className="h-36 w-full border-b border-haze"
                                    />
                                    <div className="p-7">
                                    <span className="flex h-11 w-11 items-center justify-center rounded border border-haze text-rule">
                                        <f.icon size={20} />
                                    </span>
                                    <h3 className="mt-5 text-base font-semibold text-text-hi">
                                        {f.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-text-lo">
                                        {f.body}
                                    </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </Container>
            </Section>

            <Section className="border-t border-haze bg-ink-soft/40 pt-0">
                <Container>
                    <Reveal>
                        <SectionHeading
                            eyebrow="The dashboard"
                            title="One screen. Every active name, plan-level overlaid."
                            sub="A clean dark dashboard: a sidebar of tickers with conviction tags in mono, a main candle chart with gold ruled level-lines (entry / stop / targets), and a decision card. The gold lines are the brand — they read as permanent and unmoveable, not decorative."
                        />
                    </Reveal>
                    <Reveal delay={0.12}>
                        <img
                            src="/images/platform_dashboard.png"
                            alt="Mock The Aegis Line dashboard: ticker sidebar with conviction tags and a candle chart with gold level lines"
                            className="mt-12 w-full rounded-lg border border-haze"
                            loading="lazy"
                        />
                    </Reveal>
                </Container>
            </Section>
        </>
    );
}
